import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IContentItems } from '@interfaces/settlement-fund/settlement-fund.model';
import { c11nLink } from '@shared/helpers';

import { FooterDisclaimerContent } from '../../services/content/content-jsons/footer-disclaimer-content';

type DisclaimerContentKey = keyof typeof FooterDisclaimerContent | 'vcd';
type AsteriskMode = 'all' | 'first' | 'sequence';

@Component({
  selector: 'app-footer-disclaimer',
  templateUrl: './footer-disclaimer.component.html',
  styleUrls: ['./footer-disclaimer.component.scss'],
  standalone: true,
  imports: [],
})
export class FooterDisclaimerComponent implements OnInit, OnChanges {
  @Input() disclaimer: DisclaimerContentKey | DisclaimerContentKey[] | undefined;
  @Input() contentFromMFE?: IContentItems;
  @Input() asterisk: AsteriskMode = 'all';
  @Input() fullWidth: boolean = false;

  contentItems: string[] = [];

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor() {}

  ngOnInit(): void {
    // Currently only supports 'vcd' as a standalone disclaimer. If 'vcd' is mixed with other
    // disclaimers in an array (e.g., ['IT_fluctuation_fractional', 'vcd']), the 'vcd' MFE content
    // will be filtered out and not displayed. This case doesn't exist yet but may need handling in the future.
    this.buildContentItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contentFromMFE'] && this.contentFromMFE && this.isSingleVcd()) {
      const mfeContent = Object.values(this.contentFromMFE)
        .map((value) => this.cleanMFEContent(value))
        .join('<br><br>');
      this.contentItems = [mfeContent];
    } else if (changes['disclaimer'] || changes['asterisk']) {
      this.buildContentItems();
    }
  }

  getAsterisks(index: number): string {
    switch (this.asterisk) {
      case 'first':
        return index === 0 ? '*' : '';
      case 'sequence':
        return '*'.repeat(index + 1);
      case 'all':
      default:
        return '*';
    }
  }

  private buildContentItems(): void {
    if (!this.disclaimer) {
      this.contentItems = [];
      return;
    }

    const disclaimers = Array.isArray(this.disclaimer) ? this.disclaimer : [this.disclaimer];
    this.contentItems = disclaimers
      .filter((key) => key !== 'vcd')
      .map((key, index) => {
        const rawContent = FooterDisclaimerContent[key as keyof typeof FooterDisclaimerContent];
        return this.replaceLeadingAsterisk(rawContent, index);
      });
  }

  private replaceLeadingAsterisk(content: string, index: number): string {
    if (content.startsWith('*')) {
      return this.getAsterisks(index) + content.slice(1);
    }
    return content;
  }

  private isSingleVcd(): boolean {
    return (
      this.disclaimer === 'vcd' ||
      (Array.isArray(this.disclaimer) &&
        this.disclaimer.length === 1 &&
        this.disclaimer[0] === 'vcd')
    );
  }

  cleanMFEContent = (text: string): string => {
    const regex = /\[\{([^}]+)\}\s*\{([^}]+)\}\s*\{([^}]+)\}\]/g;

    const replacement = (_match: string, linkText: string, url: string): string => {
      return c11nLink(url, linkText, true, [`c11n-link`, `c11n-link--primary`]);
    };

    return text.replace(regex, replacement).replace(/\|$/, '').replace(/\|/g, '<br><br>');
  };
}
